import { Wrench } from 'lucide-react';
import {
  DEFAULT_DECOR,
  decorOf,
  hustleOf,
  type HustleContent,
  type HustleDecorContent,
  type HustleKindContent,
} from '@london-life/contracts';
import { Input } from '@london-life/ui/components/input';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, NumberField, type NumberSpec } from './form';

type HustleNumbers = Exclude<keyof HustleContent, 'visible' | 'kinds' | 'decor'>;
const whole = (key: HustleNumbers, label: string, min: number, max: number): NumberSpec<HustleNumbers> => ({
  key,
  label,
  unit: 'number',
  min,
  max,
  whole: true,
});
const RULES: NumberSpec<HustleNumbers>[] = [
  { key: 'feeMinPence', label: 'The smallest fee', unit: 'pounds', min: 100, max: 1_000_000_000 },
  { key: 'feeMaxPence', label: 'The largest fee', unit: 'pounds', min: 100, max: 1_000_000_000 },
  whole('openHours', 'A post stays on the Board this many hours', 1, 720),
  whole('bumpHours', 'A bump once every this many hours', 1, 168),
  whole('offerHours', 'An offer waits this many hours for an answer', 1, 168),
  whole('payHours', 'A job marked done pays itself after this many hours', 1, 168),
  whole('maxActive', 'Jobs going at once (as poster, and as worker)', 1, 20),
  whole('maxApplicants', 'People who can apply to one post', 1, 200),
  whole('listDays', 'A listing for hire lasts this many days', 1, 90),
  whole('kindsMax', 'Kinds of job on one listing', 1, 8),
  whole('reportsToHide', 'Reports that take a post off the Board until the team looks', 1, 50),
];
// the decorator who works on a copy of the poster's home
type DecorNumbers = keyof HustleDecorContent;
const days = (key: DecorNumbers, label: string, min: number, max: number): NumberSpec<DecorNumbers> => ({
  key,
  label,
  unit: 'number',
  min,
  max,
  whole: true,
});
const DECOR: NumberSpec<DecorNumbers>[] = [
  days('accessDays', 'The decorator has the keys this many days', 1, 60),
  days('moreDays', 'Asking for more time adds this many days', 1, 30),
  days('mostDays', 'The most days in all', 1, 90),
  days('asksMax', 'Notes or looks turned down, per job', 0, 20),
  days('quietHours', 'Waiting for changes: the poster can call it off after this many quiet hours', 1, 720),
  {
    key: 'budgetMaxPence',
    label: 'The most a budget for new pieces can be',
    unit: 'pounds',
    min: 0,
    max: 1_000_000_000,
  },
  days('piecesMax', 'New pieces in one look', 0, 60),
];
const MAX: NumberSpec<'maxPence'> = {
  key: 'maxPence',
  label: 'The most a fee can be',
  unit: 'pounds',
  min: 100,
  max: 1_000_000_000,
};

// Hustle (as Lagos Life's Hustle): the rules for posts, offers, held fees and listings, and each kind of job. Part
// of the content draft, published with it. The charge on top is the money transfer fee (Settings → Economy).
export function HustlePage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const hustle = hustleOf(working ?? {});
  const change = (fn: (draft: HustleContent) => void) =>
    editor.edit((content) => {
      content.hustle ??= structuredClone(hustleOf(content));
      fn(content.hustle);
    });
  const decor = decorOf(hustle);
  const changeDecor = (fn: (draft: HustleDecorContent) => void) =>
    change((draft) => {
      draft.decor ??= structuredClone(DEFAULT_DECOR);
      fn(draft.decor);
    });
  const kind = (id: HustleKindContent['kind'], fn: (entry: HustleKindContent) => void) =>
    change((draft) => {
      const entry = draft.kinds.find((item) => item.kind === id);
      if (entry) fn(entry);
    });
  return (
    <section>
      <PageHeader
        title="Hustle"
        description="Players hire players (as Lagos Life's Hustle): the rules for posts, offers and held fees, and each kind of job. The charge on top is the money transfer fee. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The rules">
            <h2 className="content-subtitle">
              <Wrench size={16} aria-hidden="true" /> The rules
            </h2>
            <label className="designer-check">
              <Switch
                checked={hustle.visible}
                aria-label="Hustle open"
                onCheckedChange={(on) => change((draft) => void (draft.visible = on))}
              />{' '}
              Players can use Hustle
            </label>
            <div className="content-grid">
              {RULES.map((spec) => (
                <NumberField
                  key={spec.key}
                  id={`hustle-${spec.key}`}
                  spec={spec}
                  value={hustle[spec.key]}
                  onChange={(value) => change((draft) => void (draft[spec.key] = value))}
                />
              ))}
            </div>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The decorator">
            <h2 className="content-subtitle">🛋️ The decorator</h2>
            <p className="admin-muted">
              A decorator works on a copy of the poster's home. Nothing changes in the real home until the poster
              accepts a look; new pieces are paid for then, within the poster's budget.
            </p>
            <div className="content-grid">
              {DECOR.map((spec) => (
                <NumberField
                  key={spec.key}
                  id={`hustle-decor-${spec.key}`}
                  spec={spec}
                  value={decor[spec.key]}
                  onChange={(value) => changeDecor((draft) => void (draft[spec.key] = value))}
                />
              ))}
            </div>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Kinds of job">
            <h2 className="content-subtitle">Kinds of job</h2>
            {hustle.kinds.map((item) => (
              <div className="content-grid content-row" key={item.kind} data-testid={`hustle-kind-${item.kind}`}>
                <Field id={`hustle-${item.kind}-label`} label="What the poster wants">
                  <Input
                    id={`hustle-${item.kind}-label`}
                    value={item.label}
                    maxLength={40}
                    onChange={(e) => kind(item.kind, (entry) => void (entry.label = e.target.value))}
                  />
                </Field>
                <Field id={`hustle-${item.kind}-short`} label="The worker is a">
                  <Input
                    id={`hustle-${item.kind}-short`}
                    value={item.short}
                    maxLength={24}
                    onChange={(e) => kind(item.kind, (entry) => void (entry.short = e.target.value))}
                  />
                </Field>
                <Field id={`hustle-${item.kind}-emoji`} label="Emoji">
                  <Input
                    id={`hustle-${item.kind}-emoji`}
                    value={item.emoji}
                    maxLength={16}
                    onChange={(e) => kind(item.kind, (entry) => void (entry.emoji = e.target.value))}
                  />
                </Field>
                <NumberField
                  id={`hustle-${item.kind}-max`}
                  spec={MAX}
                  value={item.maxPence}
                  onChange={(value) => kind(item.kind, (entry) => void (entry.maxPence = value))}
                />
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`${item.label} on`}
                    onCheckedChange={(on) => kind(item.kind, (entry) => void (entry.visible = on))}
                  />{' '}
                  On
                </label>
              </div>
            ))}
          </fieldset>
        </div>
      )}
    </section>
  );
}
